import type { SxProps } from '@mui/material';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';

export const reconFlagsFiltersSeverityPillsRowSx: SxProps = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  alignItems: 'center',
};

export const reconFlagsFiltersToolbarSx: SxProps = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
};

export const reconFlagsFiltersControlsGridSx: SxProps = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(200px, 2fr)',
  },
  gap: 1.5,
  alignItems: 'flex-end',
};

export const getReconFlagsFiltersFieldShellSx = (
  sys: Tokens['sys'],
): SxProps => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    bgcolor: String(sys.color.surface),
    '& fieldset': {
      borderColor: String(sys.color.outlineVariant),
    },
    '&:hover fieldset': {
      borderColor: String(sys.color.outline),
    },
    '&.Mui-focused fieldset': {
      borderColor: String(sys.color.primary),
    },
  },
});

export const getReconFlagsFiltersStateSelectSx = (
  sys: Tokens['sys'],
): SxProps => ({
  ...getReconFlagsFiltersFieldShellSx(sys),
  '& .MuiSelect-select': { fontWeight: 700 },
});

export const getReconFlagsFiltersDefaultSelectSx = (
  sys: Tokens['sys'],
): SxProps => ({
  ...getReconFlagsFiltersFieldShellSx(sys),
  '& .MuiSelect-select': { fontWeight: 500 },
});

export const getReconFlagsFiltersSearchWrapperSx = (): SxProps => ({
  gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'auto' },
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  minWidth: 0,
});

export const getReconFlagsFiltersSearchFieldSx = (
  sys: Tokens['sys'],
): SxProps => ({
  ...getReconFlagsFiltersFieldShellSx(sys),
  flex: 1,
  minWidth: 0,
  '& input': { fontWeight: 500 },
  '& input::placeholder': {
    fontStyle: 'italic',
    opacity: 1,
    color: String(sys.color.onSurfaceVariant),
  },
});

export const severityFilterPillLabelSx: SxProps = {
  fontWeight: 500,
  color: 'inherit',
  lineHeight: 1.2,
};

export type SeverityFilterPillRootSxParams = {
  sys: Tokens['sys'];
  active: boolean;
  activeBg: string;
  activeHoverBg: string;
  inactiveBorderAndText: string;
  interactive: boolean;
};

export const getSeverityFilterPillRootSx = ({
  sys,
  active,
  activeBg,
  activeHoverBg,
  inactiveBorderAndText,
  interactive,
}: SeverityFilterPillRootSxParams): SxProps => ({
  display: 'inline-flex',
  alignItems: 'center',
  px: 2,
  py: 1,
  borderRadius: 999,
  border: '1px solid',
  borderColor: active ? 'transparent' : inactiveBorderAndText,
  bgcolor: active ? activeBg : String(sys.color.surface),
  color: active ? String(sys.color.onPrimary) : inactiveBorderAndText,
  cursor: interactive ? 'pointer' : 'default',
  fontFamily: sys.font.base,
  transition:
    'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
  ...(interactive
    ? {
        appearance: 'none',
        WebkitAppearance: 'none',
        font: 'inherit',
        textAlign: 'inherit',
        '&:hover': active
          ? { bgcolor: activeHoverBg }
          : { bgcolor: String(sys.color.neutralHover) },
      }
    : {}),
});
